import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import axios from "axios";
import { saveAs } from "file-saver";
import { useToast } from "@/context/ToastContext";
import { studyApi } from "@/lib/api";
import {
    usePreferenceScope,
    useStoredPreference,
} from "@/hooks/useStoredPreference";
import {
    listPreferencesSchema,
    type ListPreferences,
} from "@/components/listPreferences";
import { useServerList, useDebouncedSearch } from "@/hooks/useServerList";
import { useRecordDialog } from "@/hooks/useRecordDialog";
import { useClientLookup } from "@/hooks/useClientLookup";
import {
    studySortSchema,
    type StudyViewState,
} from "@/pages/Studies/listState";
import type { Tender, StudyKpis } from "@/pages/Tenders/types";

type StudyTab =
    | "all"
    | "in_study"
    | "submitted"
    | "not_submitted"
    | "awarded"
    | "not_awarded";
const VALID_STUDY_TABS: StudyTab[] = [
    "all",
    "in_study",
    "submitted",
    "not_submitted",
    "awarded",
    "not_awarded",
];

export const useStudies = () => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();
    const { tab: rawTab } = useParams<{ tab?: string }>();
    const activeTab: StudyTab = VALID_STUDY_TABS.includes(rawTab as StudyTab)
        ? (rawTab as StudyTab)
        : "in_study";
    // Data state
    const [kpis, setKpis] = useState<StudyKpis>({
        total: 0,
        total_budget: 0,
        in_study: 0,
        submitted: 0,
        not_submitted: 0,
        awarded: 0,
        not_awarded: 0,
    });

    // Keep list context per user and company when navigating between modules.
    const scope = usePreferenceScope();
    const [search, setSearch] = useStoredPreference(
        `vigepra_studies_search:${scope}`,
        z.string().max(500),
        "",
        true
    );
    const [clientFilter, setClientFilter] = useStoredPreference(
        `vigepra_studies_client:${scope}`,
        z.string(),
        "all",
        true
    );
    const [yearFilter, setYearFilter] = useStoredPreference(
        `vigepra_studies_year:${scope}`,
        z.string(),
        "all",
        true
    );
    const clientLookup = useClientLookup(clientFilter);
    const clients = clientLookup.items;
    const [dueOnly, setDueOnly] = useStoredPreference(
        `vigepra_studies_due:${scope}`,
        z.boolean(),
        false,
        true
    );
    const [sort, setSort] = useStoredPreference<StudyViewState["sort"]>(
        `vigepra_studies_sort:${scope}`,
        studySortSchema,
        { key: "submission_date", order: "desc" }
    );
    const [preferences, setPreferences] = useStoredPreference<ListPreferences>(
        `vigepra_studies_table:${scope}`,
        listPreferencesSchema,
        { density: "comfortable", hidden: ["client_type_id"] }
    );
    const [filtersOpen, setFiltersOpen] = useState(false);
    const delayedSearch = useDebouncedSearch(search);
    const listing = useServerList<Tender>("studies", "/studies", {
        tab: activeTab,
        search: delayedSearch,
        client_id: clientFilter,
        year: yearFilter,
        due_only: dueOnly ? 1 : 0,
        sort_by: sort.key,
        sort_order: sort.order,
    });
    const studies = listing.items;
    const loading = listing.loading;
    const viewState: StudyViewState = {
        tab: activeTab,
        search,
        client: clientFilter,
        year: yearFilter,
        due: dueOnly,
        sort,
        preferences,
        pageSize: listing.pagination.pageSize,
    };
    const applyView = (view: StudyViewState): void => {
        setSearch(view.search);
        setClientFilter(view.client);
        setYearFilter(view.year);
        setDueOnly(view.due);
        setSort(view.sort);
        setPreferences(view.preferences);
        navigate(`/studies/${view.tab}`);
        listing.pagination.onPageSize(view.pageSize);
    };
    const clearFilters = (): void => {
        setSearch("");
        setClientFilter("all");
        setYearFilter("all");
        setDueOnly(false);
    };

    // Modals
    const record = useRecordDialog<Tender>("studies");
    const selectedStudy = record.item;
    const setSelectedStudy = record.select;
    const [statusTargetStudy, setStatusTargetStudy] = useState<Tender | null>(
        null
    );
    const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

    const [studyBc3Target, setStudyBc3Target] = useState<Tender | null>(null);
    const [bc3ModalOpen, setBc3ModalOpen] = useState<boolean>(false);

    const [editingStudy, setEditingStudy] = useState<Tender | null>(null);
    const [formModalOpen, setFormModalOpen] = useState<boolean>(false);

    const [deleteTargetStudy, setDeleteTargetStudy] = useState<Tender | null>(
        null
    );
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const loadCommonData = useCallback(async (): Promise<void> => {
        try {
            const kpiRes = await studyApi.kpis();

            if (kpiRes?.data) {
                setKpis(kpiRes.data);
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    const loadData = async (): Promise<void> => {
        await loadCommonData();
        listing.reload();
    };
    useEffect(() => {
        void loadCommonData();
    }, [loadCommonData]);

    const handleTabChange = (tabId: StudyTab): void => {
        navigate(`/studies/${tabId}`);
    };

    const handleOpenStatus = (study: Tender): void => {
        setStatusTargetStudy(study);
        setStatusModalOpen(true);
    };

    const handleCloseStatus = (): void => {
        setStatusModalOpen(false);
        setStatusTargetStudy(null);
    };

    const handleStatusSuccess = async (
        targetStatusId: number,
        reason: string | null
    ): Promise<void> => {
        if (!statusTargetStudy) {
            return;
        }

        setIsUpdatingStatus(true);
        try {
            await studyApi.changeStatus(
                statusTargetStudy.id,
                targetStatusId,
                reason
            );
            showSuccess(t("studies.status_changed_success"));
            handleCloseStatus();
            await loadData();
        } catch (error: unknown) {
            console.error(error);
            const msg =
                (axios.isAxiosError<{ message?: string }>(error)
                    ? error.response?.data?.message
                    : error instanceof Error
                      ? error.message
                      : undefined) || t("common.error");
            showError(msg);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleDownloadBc3 = async (study: Tender): Promise<void> => {
        try {
            const blobData = await studyApi.downloadBc3(study.id);
            const blob = new Blob([blobData], {
                type: "application/octet-stream",
            });
            const fallbackName =
                study.tender?.code ||
                study.tender_code ||
                study.code ||
                study.id;
            saveAs(blob, study.bc3_file_name || `${fallbackName}.bc3`);
        } catch (error: unknown) {
            console.error(error);
            const msg =
                (axios.isAxiosError<{ message?: string }>(error)
                    ? error.response?.data?.message
                    : error instanceof Error
                      ? error.message
                      : undefined) || t("common.error");
            showError(msg);
        }
    };

    const handleOpenEdit = (study: Tender): void => {
        setEditingStudy(study);
        setFormModalOpen(true);
    };

    const handleOpenDelete = (study: Tender): void => {
        setDeleteTargetStudy(study);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async (): Promise<void> => {
        if (!deleteTargetStudy) {
            return;
        }

        setIsDeleting(true);
        try {
            await studyApi.delete(deleteTargetStudy.id);
            showSuccess(t("studies.deleted_success"));
            setDeleteModalOpen(false);
            setDeleteTargetStudy(null);
            if (selectedStudy && selectedStudy.id === deleteTargetStudy.id) {
                setSelectedStudy(null);
            }
            await loadData();
        } catch (error: unknown) {
            console.error(error);
            const msg =
                (axios.isAxiosError<{ message?: string }>(error)
                    ? error.response?.data?.message
                    : error instanceof Error
                      ? error.message
                      : undefined) || t("common.error");
            showError(msg);
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        activeTab,
        studies,
        clients,
        kpis,
        loading,
        scope,
        search,
        setSearch,
        clientFilter,
        setClientFilter,
        listing,
        record,
        clientLookup,
        yearFilter,
        setYearFilter,
        dueOnly,
        setDueOnly,
        sort,
        setSort,
        preferences,
        setPreferences,
        filtersOpen,
        setFiltersOpen,
        viewState,
        applyView,
        clearFilters,
        loadData,
        handleTabChange,
        selectedStudy,
        setSelectedStudy,
        statusTargetStudy,
        statusModalOpen,
        isUpdatingStatus,
        studyBc3Target,
        setStudyBc3Target,
        bc3ModalOpen,
        setBc3ModalOpen,
        editingStudy,
        setEditingStudy,
        formModalOpen,
        setFormModalOpen,
        deleteTargetStudy,
        setDeleteTargetStudy,
        deleteModalOpen,
        setDeleteModalOpen,
        isDeleting,
        handleOpenStatus,
        handleCloseStatus,
        handleStatusSuccess,
        handleDownloadBc3,
        handleOpenEdit,
        handleOpenDelete,
        handleConfirmDelete,
    };
};
